"use client";

import { useD3ClientPage } from "@/hook/useD3ClientPage";
import { useD3Fetch } from "@/hook/useD3Fetch";
import { useSession } from "@/hook/useSession";
import { Link } from "@/i18n/routing";
import { DebatesDetail, DebatesInfo, IDebateVotes, IFollows, Position, Stance } from "@/types";
import { getCalculatedHeight, getCommonTextStyles, resolveImageUrl } from "@/utils/dabateUtil";
import { formatCount, formatRelativeTime } from "@/utils/formatUtil";
import * as Icons from "@mui/icons-material";
import {
  AppBar,
  Box,
  Card,
  CardContent,
  Container,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import CommentsContainer from "../comments/CommentsContainer";
import CardImage from "../common/CardImage";
import { CategoryChip } from "../common/CategoryChip";
import DebateAvatar from "../common/DebateAvatar";
import DeleteToggleButton from "../common/DeleteToggleButton";
import FollowToggleButton from "../common/FollowToggleButton";
import LikeDislikeButton from "../common/LikeDislikeButton";
import PullToRefreshWrapper from "../common/PullToRefreshWrapper";
import ShareButton from "../common/ShareButton";
import TitleAppBar from "../common/TitleAppBar";
import { getPositionStyle } from "../common/TitleTextBox";
import VoteButtons from "../common/VoteButtons";
import VoteDetail from "../common/VoteDetail";
import VotingPeriod from "../common/VotingPeriod";
import dayjs from "dayjs";
import { useLocale, useTranslations } from "next-intl";
import { safeT } from "@/utils/safeT";
import GoogleTranslateShowMoreText from "../common/GoogleTranslateShowMoreText";
import GoogleTranslateText from "../common/GoogleTranslateText";

export default function DebateDetail({ initialData, lang }: { initialData: DebatesDetail; lang: string }) {
  const theme = useTheme();
  const [item, setItem] = useState(initialData);
  const locale = useLocale();
  const { session } = useSession();
  const d3Fetch = useD3Fetch();
  const t = useTranslations();

  const handleDebateLike = (lang: string, id: string, state: { isLiked: boolean; isDisliked: boolean }) => {
    d3Fetch.post("/debates/like", { lang, debateId: id, ...state });
  };

  const handleDebateDislike = (lang: string, id: string, state: { isLiked: boolean; isDisliked: boolean }) => {
    d3Fetch.post("/debates/dislike", { lang, debateId: id, ...state });
  };

  const today = dayjs().format("YYYYMMDD");
  const isVotePeriod = today >= item.debate.startDate && today <= item.debate.endDate;
  const isLoggedIn = !!session?.user?.id;

  /**
   * 토론 삭제
   * @param debateId
   * @param lang
   * @param del
   */
  const toggleDelete = (debateId: string, lang: string, del: boolean) => {
    if (del) {
      d3Fetch.del(`/debates/delete/${lang}/${debateId}`);
    } else {
      d3Fetch.patch(`/debates/delete/${lang}/${debateId}`, null);
    }
  };

  /**
   * 구독 버튼 follow
   * @param followedId 토론글 작성자를 로그인 사용자가 팔로우
   * @param lang
   * @param follow
   */
  const toggleFollow = (followedId: string, lang: string, follow: boolean) => {
    if (follow) {
      d3Fetch.post(`/debates/follows`, {
        followedId,
        lang,
        status: "active",
      } as IFollows);
    } else {
      d3Fetch.patch(`/debates/follows`, {
        followedId,
        lang,
        status: "inactive",
      } as IFollows);
    }
  };

  /**
   * 투표 하기
   * @param debateId
   * @param lang
   * @param stance
   */
  const handleVote = async (debateId: string, lang: string, stance: Stance) => {
    const { success } = await d3Fetch.post("/debates/vote", {
      debateId,
      lang,
      stance,
    } as IDebateVotes);

    if (success) {
      const { data } = await d3Fetch.get<DebatesInfo>(`/debates/${lang}/${debateId}`);
      setItem(data as DebatesInfo);
    }
  };

  return (
    <Container sx={{ px: 0 }} maxWidth="md">
      <TitleAppBar title={item.debate.title ?? "Debate"} showNoti={false} showSearch={false} />
      <PullToRefreshWrapper />
      <Box sx={{ my: 0 }}>
        <Card>
          <Box sx={{ position: "relative" }}>
            <CardImage imagePath={resolveImageUrl(item.debate.thumbnailUrl || "")} height={{ xs: 207, sm: 267 }} />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
            >
              <Typography
                sx={
                  {
                    cursor: "pointer",
                    paddingX: 0.5,
                    paddingY: 1,
                    position: "inherit",
                    width: "100%",
                    whiteSpace: "pre-line",
                    height: getCalculatedHeight(item.debate.fontSize ?? 26),
                    ...getCommonTextStyles(
                      item.debate.fontColor,
                      item.debate.textAlign,
                      item.debate.fontSize,
                      item.debate.textBgcolor
                    ),
                    ...getPositionStyle((item.debate.textPosition as Position) ?? "middle-center"), // 안전하게 스타일 적용
                  } as SxProps<Theme>
                }
              >
                {item.debate.title}
              </Typography>
            </Box>
          </Box>
          <CardContent>
            <Typography
              variant="h5"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word", // 길게 붙은 단어도 잘림 방지
              }}
            >
              <GoogleTranslateText sourceLang={item.debate.lang} targetLang={locale} text={item.debate.content} />
            </Typography>

            <Stack direction="row" spacing={2} alignItems="start" sx={{ marginTop: 1 }}>
              <DebateAvatar src={item.user.picture} name={item.user.nickname ?? ""} size={36} />
              <Box flexGrow={1}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                    <Typography variant="body1" color="text.primary">
                      {item.user.nickname}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.user.followerCount ? formatCount(item.user.followerCount, locale) : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {safeT(t, "ui.label.views", {
                        defaultMessage: "조회수",
                      })}{" "}
                      {formatCount(item.statistics.readCount, locale)} •{" "}
                      {formatRelativeTime(item.debate.updatedAt || "", locale)}
                      {item.debate.createdAt !== item.debate.updatedAt &&
                        `(${safeT(t, "ui.label.edited", {
                          defaultMessage: "수정됨",
                        })})`}
                    </Typography>
                    <CategoryChip code={item.debate.category} locale={locale} fontSize={10} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LikeDislikeButton
                      lang={item.debate.lang}
                      id={item.debate.id}
                      initialLikeCount={item.statistics.likeCount}
                      initialDislikeCount={item.statistics.dislikeCount}
                      fontSize={20}
                      initialLiked={item.likes?.isLiked}
                      initialDisliked={item.likes?.isDisliked}
                      onDislike={(lang, id, state) => {
                        //console.log("onDislike", lang, id, state);
                        handleDebateDislike(lang, id, state);
                      }}
                      onLike={(lang, id, state) => {
                        //console.log("onLike", lang, id, state);
                        handleDebateLike(lang, id, state);
                      }}
                    />
                    <ShareButton title={item.debate.title} text={item.debate.content} />
                    {session?.user?.id === item.user.id ? (
                      <>
                        <IconButton
                          size="small"
                          aria-label="edit debate"
                          LinkComponent={Link}
                          href={`/edit/${item.debate.id}?lang=${item.debate.lang}`}
                        >
                          <Icons.EditNote sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                        </IconButton>
                        <DeleteToggleButton id={item.debate.id} lang={item.debate.lang} onToggle={toggleDelete} />
                      </>
                    ) : null}
                    {session && session?.user?.id !== item.user.id ? (
                      <FollowToggleButton
                        followedId={item.debate.createdBy}
                        initialFollow={item.debate.isFollow}
                        lang={item.debate.lang}
                        onToggle={toggleFollow}
                      />
                    ) : null}
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Box
          sx={{
            backgroundColor: theme.palette.background.customBg,
            borderRadius: { xs: 0, sm: "6px", md: "6px", lg: "8px", xl: "8px" },
            py: 2,
            px: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <Box sx={{ width: "70%" }}>
              <VotingPeriod
                startDate={item.debate.startDate}
                endDate={item.debate.endDate}
                label={safeT(t, "ui.label.votingPeriod", {
                  defaultMessage: "투표 기간",
                })}
              />
            </Box>
            {item.votes ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography component="span">{formatRelativeTime(item.votes?.createdAt ?? "", locale)}</Typography>
                <Typography
                  component="span"
                  sx={{
                    py: "1px",
                    px: "6px",
                    backgroundColor: item.votes?.stance === "pros" ? "#1E9E23" : "#F01616",
                    borderRadius: "5px",
                    color: "white",
                    fontWeight: 500,
                  }}
                >
                  {safeT(t, "ui.label.vote", {
                    defaultMessage: "투표",
                  })}
                </Typography>
              </Box>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1} mt={2}>
            {item.votes || !isVotePeriod ? (
              <VoteDetail debate={item} />
            ) : (
              <VoteButtons disabled={!isLoggedIn} debate={item.debate} onVote={handleVote} />
            )}
          </Stack>
        </Box>
        {/**  댓글 목록 */}
        <CommentsContainer lang={item.debate.lang} debateId={item.debate.id} debateUser={item.user} />
      </Box>
      <Toolbar />
      <Toolbar />
    </Container>
  );
}
